import { z } from "zod";
import {
  EXPENSE_AUTO_APPROVE_MINOR_KEY as DOMAIN_AUTO_APPROVE_KEY,
  EXPENSE_AUTO_MODE_ENABLED_KEY as DOMAIN_AUTO_MODE_KEY,
  EXPENSE_VOTE_REQUIRED_MINOR_KEY as DOMAIN_VOTE_REQUIRED_KEY,
  isDecisionThresholdMinor,
} from "@goppy/domain";

/**
 * Contract config NGƯỠNG TIỀN quyết định mode duyệt chi (US-E17.F1-0188-S02 · @UC9251).
 *
 * 3 khoá registry (`system_settings`, override được ở Group.settings / Fund.settings):
 *  - `expense_auto_mode_enabled` (bool) — BẬT auto-mode theo ngưỡng. **Default FALSE** ⇒ quỹ cũ giữ
 *    nguyên `expense_decision_mode` tĩnh (KHÔNG regress).
 *  - `expense_auto_approve_threshold_minor` (string BigInt) — dưới ngưỡng → admin/treasurer tự duyệt.
 *  - `expense_vote_required_threshold_minor` (string BigInt) — từ ngưỡng này trở lên → BẮT BUỘC vote.
 *
 * TIỀN = **chuỗi BigInt minor** (B1 no-float) — KHÔNG `z.number()`. Key wire + type-guard DERIVE từ
 * `@goppy/domain` decision-mode (một nguồn sự thật, không nhân bản).
 */

export const EXPENSE_AUTO_MODE_ENABLED_KEY = DOMAIN_AUTO_MODE_KEY;
export const EXPENSE_AUTO_APPROVE_MINOR_KEY = DOMAIN_AUTO_APPROVE_KEY;
export const EXPENSE_VOTE_REQUIRED_MINOR_KEY = DOMAIN_VOTE_REQUIRED_KEY;

/** Thứ tự ổn định cho UI/seed/test. */
export const DECISION_MODE_CONFIG_KEY_LIST = [
  EXPENSE_AUTO_MODE_ENABLED_KEY,
  EXPENSE_AUTO_APPROVE_MINOR_KEY,
  EXPENSE_VOTE_REQUIRED_MINOR_KEY,
] as const;

/** Ngưỡng tiền = chuỗi BigInt minor ≥ 0 (guard domain — no-float, no `z.number`). */
export const decisionThresholdMinorPatchSchema = z
  .string()
  .refine((v) => isDecisionThresholdMinor(v), {
    message: "Ngưỡng tiền phải là số nguyên không âm (đơn vị minor, dạng chuỗi).",
  });

export const expenseAutoModeEnabledPatchSchema = z.boolean();

/**
 * Body patch config ngưỡng (fund/group). `.strict()` + ≥1 key + superRefine chéo:
 * `autoApprove ≤ voteRequired` (fail-loud — đảo ngược = config vô nghĩa, resolver sẽ fail-closed majority).
 * Kiểm chéo CHỈ khi cả 2 key cùng có mặt trong patch (patch từng phần → service validate lại effective).
 */
export const fundDecisionThresholdPatchSchema = z
  .object({
    expenseAutoModeEnabled: expenseAutoModeEnabledPatchSchema.optional(),
    expenseAutoApproveThresholdMinor: decisionThresholdMinorPatchSchema.optional(),
    expenseVoteRequiredThresholdMinor: decisionThresholdMinorPatchSchema.optional(),
  })
  .strict()
  .refine((body) => Object.keys(body).length > 0, {
    message: "Cần ít nhất 1 thiết lập để cập nhật",
  })
  .superRefine((body, ctx) => {
    const auto = body.expenseAutoApproveThresholdMinor;
    const vote = body.expenseVoteRequiredThresholdMinor;
    if (auto === undefined || vote === undefined) return;
    if (BigInt(auto) > BigInt(vote)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["expenseAutoApproveThresholdMinor"],
        message: "Ngưỡng tự duyệt phải ≤ ngưỡng bắt buộc biểu quyết",
      });
    }
  });

export type FundDecisionThresholdPatch = z.infer<typeof fundDecisionThresholdPatchSchema>;

/** Effective config ngưỡng của 1 quỹ (fund > group > system). Tiền = string BigInt. */
export const decisionModeConfigResponseSchema = z
  .object({
    expenseAutoModeEnabled: expenseAutoModeEnabledPatchSchema,
    expenseAutoApproveThresholdMinor: decisionThresholdMinorPatchSchema,
    expenseVoteRequiredThresholdMinor: decisionThresholdMinorPatchSchema,
  })
  .strict();

export type DecisionModeConfigResponse = z.infer<typeof decisionModeConfigResponseSchema>;
